import { DOCUMENT, inject, Injectable } from '@angular/core';

const themeIds = new Set([
  'theme-default', 'theme-brand', 'theme-teal',
  'theme-rose', 'theme-purple', 'theme-amber',
]);

@Injectable({ providedIn: 'root' })
export class ThemeStylesheetLoaderService {
  private readonly document = inject(DOCUMENT);
  private activeTheme = '';
  private activeLink?: HTMLLinkElement;
  private pendingTheme = '';
  private pendingLoad?: Promise<string>;
  private requestId = 0;

  load(theme: string | undefined): Promise<string> {
    const themeId = theme && themeIds.has(theme) ? theme : 'theme-default';
    if (this.pendingTheme === themeId && this.pendingLoad) {
      return this.pendingLoad;
    }
    if (this.activeTheme === themeId) {
      ++this.requestId;
      this.pendingTheme = '';
      this.pendingLoad = undefined;
      return Promise.resolve(themeId);
    }

    const requestId = ++this.requestId;
    this.pendingTheme = themeId;
    this.pendingLoad = this.loadStylesheet(themeId, requestId).catch(() => {
      if (requestId !== this.requestId) {
        return this.activeTheme || 'theme-default';
      }
      if (themeId === 'theme-default') {
        return this.activeTheme || 'theme-default';
      }
      return this.load('theme-default');
    });
    return this.pendingLoad;
  }

  private loadStylesheet(themeId: string, requestId: number): Promise<string> {
    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${themeId}.css`;
    link.dataset.portalTheme = themeId;

    return new Promise<string>((resolve, reject) => {
      link.addEventListener('load', () => {
        if (requestId === this.requestId) {
          this.activeLink?.remove();
          this.activeLink = link;
          this.activeTheme = themeId;
          this.pendingTheme = '';
          this.pendingLoad = undefined;
        } else {
          link.remove();
        }
        resolve(requestId === this.requestId ? themeId : this.activeTheme);
      }, { once: true });
      link.addEventListener('error', () => {
        link.remove();
        if (requestId === this.requestId) {
          this.pendingTheme = '';
          this.pendingLoad = undefined;
        }
        reject(new Error(`Failed to load ${themeId}.css`));
      }, { once: true });
      this.document.head.appendChild(link);
    });
  }
}
